import Cookies from "js-cookie";
import { useEffect, useRef } from "react";
import Drawers from "@components/views/Drawer/Drawers";
import UserLangModal from "@components/views/UserLangModal";
import Meta from "./Meta";
import Head from "./Head";
import Header from "./Header";
import Flashes from "./Flashes";
import Footer from "./Footer";
import HeaderOnboarding from "./HeaderOnboarding";
import HeaderAuth from "./HeaderAuth";
import TourGuide from "./TourGuide/TourGuide";
import isServer from "@utils/is-server";

const Layout = ({ children, headerBehaviors = {} }) => {
  const {
    noNavbar,
    noFooter,
    noHead,
    onboarding,
    auth,
    context,
    tourGuideAllowed,
    routerTransition,
  } = headerBehaviors;

  const mainRef = useRef(null);

  let HeaderComponent;
  if (noNavbar) {
    HeaderComponent = null;
  } else if (onboarding) {
    HeaderComponent = HeaderOnboarding;
  } else if (auth) {
    HeaderComponent = HeaderAuth;
  } else {
    HeaderComponent = Header;
  }

  function saveTheSwapNotationCookie() {
    if (!Cookies.get("swap_notation")) return;

    const cookieObj = JSON.parse(Cookies.get("swap_notation"));
    const { startingTime, userRefusedNotation } = cookieObj;
    Cookies.set(
      "swap_notation",
      {
        startingTime,
        userRefusedNotation,
      },
      { expires: 1 },
    );
  }

  useEffect(() => {
    saveTheSwapNotationCookie();
  }, []);

  let FooterComponent;
  if (noFooter || onboarding) {
    FooterComponent = null;
  } else {
    FooterComponent = Footer;
  }

  useEffect(() => {
    if (isServer() || !mainRef.current) return;

    function addTransitionOpacityClass() {
      mainRef?.current?.classList?.add("transition-leave-to");
    }

    function removeTransitionOpacityClass() {
      mainRef?.current?.classList?.remove("transition-leave-to");
    }

    function preserveScrollPosition() {
      window.scrollTo(0, window.scrollY);
    }

    function removeTransitionEndListener(eventHandler) {
      mainRef?.current?.removeEventListener("transitionend", eventHandler);
    }

    function addTransitionEndListener(eventHandler) {
      mainRef?.current?.addEventListener("transitionend", eventHandler);
    }

    function setupSafetyTimeout(eventHandler) {
      mainRef?.current;

      setTimeout(() => {
        if (mainRef?.current?.classList?.contains("transition-leave-to")) {
          removeTransitionOpacityClass();
          removeTransitionEndListener(eventHandler);
        }
      }, 1000);
    }

    const startPopStateTransition = () => {
      // Synchronizes DOM operations with browser rendering for smoother animations.
      window.requestAnimationFrame(() => {
        if (!mainRef.current) return;

        preserveScrollPosition();

        let timeoutId;

        const transitionEndHandler = (e) => {
          if (e.target === mainRef.current && e.propertyName === "opacity") {
            removeTransitionOpacityClass();
            removeTransitionEndListener(transitionEndHandler);
            clearTimeout(timeoutId);
          }
        };

        addTransitionEndListener(transitionEndHandler);

        timeoutId = setupSafetyTimeout(transitionEndHandler);

        addTransitionOpacityClass();
      });
    };

    function addPopStateListener() {
      window.addEventListener("popstate", startPopStateTransition);
    }

    function removePopStateListener() {
      window.removeEventListener("popstate", startPopStateTransition);
    }

    removeTransitionOpacityClass();

    if (!routerTransition) return;

    addPopStateListener();

    return () => removePopStateListener();
  }, [routerTransition]);

  return (
    <div className="layout-container">
      <Meta />
      {!noHead && <Head />}
      <div className="min-vh-100 d-flex flex-column">
        {HeaderComponent && <HeaderComponent context={context} />}
        <Flashes />
        <main
          ref={mainRef}
          className="flex-grow-1 d-flex flex-column"
        >
          {children}
        </main>
      </div>
      {FooterComponent && <FooterComponent />}
      <Drawers />
      <UserLangModal />
      <TourGuide tourGuideAllowed={tourGuideAllowed} />
    </div>
  );
};

export default Layout;
